import { TokenRequest, TokenResponse } from '../types/chat.types';
import { getNonReactiveState } from '@store';
import frontendConfig from '../../../config.frontend';

/**
 * TokenService - Handles Stream Chat JWT token generation and management
 * Based on Stream's token authentication best practices:
 * https://getstream.io/chat/docs/react/tokens_and_authentication/
 *
 * In production, tokens MUST be generated server-side for security.
 * This service provides a token provider function for Stream Chat client.
 */
class TokenService {
  private static instance: TokenService;
  private tokenCache: Map<string, { token: string; expiresAt: Date }> = new Map();
  private isDevMode: boolean = import.meta.env.DEV;

  private constructor() {
    // Private constructor for singleton pattern
    if (this.isDevMode) {
      console.warn('[TokenService] Running in development mode. Remember to use server-side tokens in production!');
    }
  }

  public static getInstance(): TokenService {
    if (!TokenService.instance) {
      TokenService.instance = new TokenService();
    }
    return TokenService.instance;
  }

  /**
   * Request a Stream Chat token from the backend
   * @param request - Token request parameters
   * @returns Token response with JWT and expiration
   */
  public async requestToken(request: TokenRequest): Promise<TokenResponse> {
    const cacheKey = `${request.userId}_${request.role}`;

    // Check cache first
    const cached = this.tokenCache.get(cacheKey);
    if (cached && cached.expiresAt > new Date()) {
      console.log('[TokenService] Returning cached token for:', request.userId);
      return {
        token: cached.token,
        userId: request.userId,
        expiresAt: cached.expiresAt,
      };
    }

    try {
      // TODO: Replace this with actual GraphQL mutation or REST API call
      // This is a placeholder for the backend integration
      const response = await this.fetchTokenFromBackend(request);

      // Cache the token
      if (response.expiresAt) {
        this.tokenCache.set(cacheKey, {
          token: response.token,
          expiresAt: response.expiresAt,
        });
      }

      console.log('[TokenService] Token generated for:', request.userId);
      return response;
    } catch (error) {
      console.error('[TokenService] Failed to generate token:', error);
      throw error;
    }
  }

  /**
   * Fetch token from backend API
   * @param request - Token request parameters
   * @private
   */
  private async fetchTokenFromBackend(request: TokenRequest): Promise<TokenResponse> {
    console.log('[TokenService] Requesting token from backend function');

    try {
      // Get the current user's Firebase JWT token for authentication
      const state = getNonReactiveState();
      if (!state.user) {
        throw new Error('User not authenticated');
      }

      const firebaseToken = await state.user.getToken();

      // Call Netlify function to generate Stream token. hopdriveEventsUrl is the
      // per-env event-handlers base URL from config (no trailing slash), so this
      // resolves to the PR's own alias on preview builds instead of shared events-test.
      const response = await fetch(
        `${frontendConfig.hopdriveEventsUrl}/.netlify/functions/app-generateStreamChatToken`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${firebaseToken}`,
          },
          body: JSON.stringify({}), // Function extracts user info from JWT
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const tokenData = await response.json();

      console.log('[TokenService] Token received from backend');

      return {
        token: tokenData.token,
        userId: tokenData.userId,
        expiresAt: new Date(tokenData.expiresAt),
      };
    } catch (error) {
      console.error('[TokenService] Failed to fetch token from backend:', error);
      throw error;
    }
  }

  /**
   * Token provider function for Stream Chat client
   * This is the recommended pattern from Stream documentation
   * Returns a function that can be passed to client.connectUser()
   *
   * @param userId - User ID to generate token for
   * @param role - User role (admin/driver)
   */
  public getTokenProvider(userId: string, role: 'admin' | 'driver' = 'admin'): () => Promise<string> {
    return async () => {
      console.log('[TokenService] Token provider called for:', userId);

      try {
        const response = await this.requestToken({ userId, role });
        return response.token;
      } catch (error) {
        console.error('[TokenService] Token provider failed:', error);
        throw error;
      }
    };
  }

  /**
   * Refresh an expiring token
   * @param userId - User ID to refresh token for
   */
  public async refreshToken(userId: string): Promise<TokenResponse> {
    console.log('[TokenService] Refreshing token for:', userId);

    // Clear cached token to force refresh
    this.clearTokenCache(userId);

    // Request new token
    return this.requestToken({
      userId,
      role: 'admin', // TODO: Get actual role from user context
    });
  }

  /**
   * Validate if a token is still valid
   * @param token - Token to validate
   * @param userId - User ID the token belongs to
   */
  public isTokenValid(token: string, userId: string): boolean {
    const cached = Array.from(this.tokenCache.entries()).find(
      ([key, value]) => key.startsWith(userId) && value.token === token
    );

    if (!cached) {
      return false;
    }

    const [, cacheData] = cached;
    return cacheData.expiresAt > new Date();
  }

  /**
   * Clear token cache for a specific user
   * @param userId - User ID to clear cache for
   */
  public clearTokenCache(userId?: string): void {
    if (userId) {
      // Clear specific user's tokens
      Array.from(this.tokenCache.keys())
        .filter(key => key.startsWith(userId))
        .forEach(key => this.tokenCache.delete(key));
      console.log('[TokenService] Cache cleared for user:', userId);
    } else {
      // Clear all cached tokens
      this.tokenCache.clear();
      console.log('[TokenService] All token cache cleared');
    }
  }

  /**
   * Get token expiration time
   * @param userId - User ID to check
   */
  public getTokenExpiration(userId: string): Date | null {
    const cached = Array.from(this.tokenCache.entries()).find(([key]) => key.startsWith(userId));

    if (!cached) {
      return null;
    }

    const [, cacheData] = cached;
    return cacheData.expiresAt;
  }

  /**
   * Set up automatic token refresh
   * @param userId - User ID to set up refresh for
   * @param callback - Callback to execute when token is refreshed
   */
  public setupAutoRefresh(userId: string, callback: (token: TokenResponse) => void): NodeJS.Timeout | null {
    const expiration = this.getTokenExpiration(userId);

    if (!expiration) {
      console.warn('[TokenService] No token expiration found for auto-refresh setup');
      return null;
    }

    // Refresh 5 minutes before expiration
    const refreshTime = expiration.getTime() - Date.now() - 5 * 60 * 1000;

    if (refreshTime <= 0) {
      // Token already expired or expiring soon, refresh immediately
      this.refreshToken(userId).then(callback);
      return null;
    }

    // Set up timer for refresh
    const timerId = setTimeout(async () => {
      try {
        const newToken = await this.refreshToken(userId);
        callback(newToken);

        // Set up next refresh
        this.setupAutoRefresh(userId, callback);
      } catch (error) {
        console.error('[TokenService] Auto-refresh failed:', error);
      }
    }, refreshTime);

    console.log('[TokenService] Auto-refresh scheduled for:', new Date(Date.now() + refreshTime));
    return timerId;
  }
}

export const tokenService = TokenService.getInstance();
